import { useState, useEffect, useMemo } from 'react';
import { Settings2, X, ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { LocationAutocomplete, LocationValue } from '@/components/ui/location-autocomplete';
import { GradingCompanyCombobox } from '@/components/shared/GradingCompanyCombobox';
import {
  MobileFilterSheetShell,
  FilterSection,
  FilterChipRow,
  FilterChipMulti,
  PriceRangeSection,
} from '@/components/filters';
import { DISTANCE_OPTIONS, DistanceOption } from '@/lib/distanceUtils';
import { POKEMON_LANGUAGES } from '@/constants/pokemonLanguages';
import { TRADING_CARD_GAMES } from '@/constants/tradingCardGames';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';
import {
  SINGLES_CONDITION_LABELS,
  SEALED_CONDITION_LABELS,
  CATEGORY_LABELS,
  PRODUCT_TYPE_LABELS,
  ListingCategory,
  SinglesCondition,
  SealedCondition,
} from '@/types/listing';

type FilterMode = 'browse' | 'my-items';

interface MobileInventoryFilterSheetProps {
  mode?: FilterMode;
  category: string[];
  setCategory: (value: string[]) => void;
  condition: string;
  setCondition: (value: string) => void;
  priceMin: number;
  setPriceMin: (value: number) => void;
  priceMax: number;
  setPriceMax: (value: number) => void;
  location: LocationValue | null;
  setLocation: (value: LocationValue | null) => void;
  distanceRadius: DistanceOption;
  setDistanceRadius: (value: DistanceOption) => void;
  includeShipping: boolean;
  setIncludeShipping: (value: boolean) => void;
  gradingCompany: string;
  setGradingCompany: (value: string) => void;
  gradeMin: number;
  setGradeMin: (value: number) => void;
  gradeMax: number;
  setGradeMax: (value: number) => void;
  premiumOnly: boolean;
  setPremiumOnly: (value: boolean) => void;
  productType: string;
  setProductType: (value: string) => void;
  selectedGame: string;
  setSelectedGame: (value: string) => void;
  selectedLanguages: string[];
  setSelectedLanguages: (value: string[]) => void;
  visibility?: string[];
  setVisibility?: (value: string[]) => void;
  availableGames?: string[];
  availableCategories?: string[];
  availableConditions?: string[];
  availableLanguages?: string[];
  availableGradingCompanies?: string[];
  availableProductTypes?: string[];
  hasSlabs?: boolean;
  onClearFilters: () => void;
  /** Optional custom trigger element. When provided, replaces the built-in icon button. */
  trigger?: React.ReactNode;
}

// Top 5 TCGs surfaced by default; everything else lives behind "More games".
const TOP_GAMES = ['pokemon', 'onepiece', 'mtg'] as const;
// Top 5 languages surfaced by default; everything else lives behind "More languages".
const TOP_LANGUAGES = ['english', 'japanese', 'chinese_simplified', 'chinese_traditional', 'korean'];

export function MobileInventoryFilterSheet({
  mode = 'browse',
  category,
  setCategory,
  condition,
  setCondition,
  priceMin,
  setPriceMin,
  priceMax,
  setPriceMax,
  location,
  setLocation,
  distanceRadius,
  setDistanceRadius,
  includeShipping,
  setIncludeShipping,
  gradingCompany,
  setGradingCompany,
  gradeMin,
  setGradeMin,
  gradeMax,
  setGradeMax,
  productType,
  setProductType,
  selectedGame,
  setSelectedGame,
  selectedLanguages,
  setSelectedLanguages,
  visibility,
  setVisibility,
  availableGames,
  availableCategories,
  availableConditions,
  availableLanguages,
  availableGradingCompanies,
  availableProductTypes,
  hasSlabs,
  onClearFilters,
  trigger,
}: MobileInventoryFilterSheetProps) {
  const isMyItemsMode = mode === 'my-items';
  // Condition/product-type sub-filters only make sense when exactly one category is picked.
  const catSel = category.length === 1 ? category[0] : 'all';
  const [open, setOpen] = useState(false);
  const [showAllGames, setShowAllGames] = useState(false);
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Lots: clamp max price; auto-reset condition when category change makes it invalid
  useEffect(() => {
    if (catSel === 'lots' && priceMax !== 1000) {
      setPriceMax(1000);
    }
    if (catSel === 'singles' && condition !== 'all' && !(condition in SINGLES_CONDITION_LABELS)) {
      setCondition('all');
    } else if (catSel === 'sealed' && condition !== 'all' && !(condition in SEALED_CONDITION_LABELS)) {
      setCondition('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Selected-but-hidden game forces expansion so the active chip is never invisible.
  const selectedGameOutsideTop =
    selectedGame !== 'all' && !(TOP_GAMES as readonly string[]).includes(selectedGame);
  const gamesExpanded = showAllGames || selectedGameOutsideTop;

  const gameOptions = useMemo(() => {
    const all = { value: 'all', label: 'All' };
    const scopedGames = availableGames
      ? TRADING_CARD_GAMES.filter((g) => availableGames.includes(g.value))
      : TRADING_CARD_GAMES;
    if (gamesExpanded) {
      return [all, ...scopedGames.map((g) => ({ value: g.value, label: g.label }))];
    }
    const byValue = new Map(scopedGames.map((g) => [g.value, g] as const));
    const top = TOP_GAMES
      .map((v) => byValue.get(v))
      .filter((g): g is (typeof TRADING_CARD_GAMES)[number] => Boolean(g))
      .map((g) => ({ value: g.value, label: g.label }));
    return [all, ...top];
  }, [gamesExpanded, availableGames]);

  const hasMoreGames = useMemo(() => {
    const scoped = availableGames
      ? TRADING_CARD_GAMES.filter((g) => availableGames.includes(g.value))
      : TRADING_CARD_GAMES;
    return scoped.length > TOP_GAMES.length;
  }, [availableGames]);

  const categoryOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(Object.entries(CATEGORY_LABELS) as [ListingCategory, string][])
        .filter(([value]) => !availableCategories || availableCategories.includes(value))
        .map(([value, label]) => ({ value, label })),
    ],
    [availableCategories],
  );

  const productTypeOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...Object.entries(PRODUCT_TYPE_LABELS)
        .filter(([value]) => !availableProductTypes || availableProductTypes.includes(value))
        .map(([value, label]) => ({ value, label })),
    ],
    [availableProductTypes],
  );

  const conditionOptions = useMemo(() => {
    const base = [{ value: 'all', label: 'All' }];
    let opts: { value: string; label: string }[];
    if (catSel === 'singles') {
      opts = [
        ...base,
        ...(Object.entries(SINGLES_CONDITION_LABELS) as [SinglesCondition, string][]).map(
          ([value, label]) => ({ value, label }),
        ),
      ];
    } else if (catSel === 'sealed') {
      opts = [
        ...base,
        ...(Object.entries(SEALED_CONDITION_LABELS) as [SealedCondition, string][]).map(
          ([value, label]) => ({ value, label }),
        ),
      ];
    } else {
      opts = [
        ...base,
        { value: 'mint', label: 'Mint' },
        { value: 'near_mint', label: 'Near Mint' },
        { value: 'lightly_played', label: 'Lightly Played' },
        { value: 'factory_sealed', label: 'Factory Sealed' },
        { value: 'sealed', label: 'Sealed' },
      ];
    }
    if (availableConditions) {
      opts = opts.filter((o) => o.value === 'all' || availableConditions.includes(o.value));
    }
    return opts;
  }, [category, availableConditions]);

  const statusOptions = useMemo(
    () => [
      { value: 'listed', label: 'Listed' },
      { value: 'private', label: 'Unlisted' },
      { value: 'sold', label: 'Sold' },
      { value: 'priced', label: 'Priced' },
      { value: 'taking_offers', label: 'Taking offers' },
    ],
    [],
  );

  const languagesExpanded =
    showAllLanguages ||
    selectedLanguages.some((l) => !TOP_LANGUAGES.includes(l));

  const languageOptions = useMemo(() => {
    const scopedLangs = availableLanguages
      ? POKEMON_LANGUAGES.filter((l) => availableLanguages.includes(l.value))
      : POKEMON_LANGUAGES;
    if (languagesExpanded) {
      return scopedLangs.map((l) => ({
        value: l.value,
        label: `${l.flag} ${l.label}`,
      }));
    }
    const top = scopedLangs.filter((l) => TOP_LANGUAGES.includes(l.value));
    return top.map((l) => ({ value: l.value, label: `${l.flag} ${l.label}` }));
  }, [languagesExpanded, availableLanguages]);

  const hasMoreLanguages = useMemo(() => {
    const scoped = availableLanguages
      ? POKEMON_LANGUAGES.filter((l) => availableLanguages.includes(l.value))
      : POKEMON_LANGUAGES;
    return scoped.some((l) => !TOP_LANGUAGES.includes(l.value));
  }, [availableLanguages]);

  const showGameSection = gameOptions.length > 1; // "All" + ≥1 real game
  const showCategorySection = categoryOptions.length > 2;
  const showConditionSection = catSel !== 'slabs' && conditionOptions.length > 1;
  const showProductTypeRow = catSel === 'sealed' && productTypeOptions.length > 2;
  const showGradingSections = hasSlabs !== false;
  const showLanguageSection = languageOptions.length > 0;

  const currentDistanceIndex = (() => {
    const idx = DISTANCE_OPTIONS.findIndex((opt) => opt.value === distanceRadius);
    return idx === -1 ? DISTANCE_OPTIONS.length - 1 : idx;
  })();
  const currentDistanceLabel = DISTANCE_OPTIONS[currentDistanceIndex]?.label || 'Any distance';

  // Count filters inside the "More filters" accordion so we can show a badge
  // and auto-expand when something in there is set.
  const advancedFilterCount =
    (condition !== 'all' && catSel !== 'slabs' ? 1 : 0) +
    (catSel === 'sealed' && productType !== 'all' ? 1 : 0) +
    (gradingCompany !== 'all' ? 1 : 0) +
    (gradeMin !== 1 || gradeMax !== 10 ? 1 : 0) +
    (!isMyItemsMode && location ? 1 : 0) +
    (!isMyItemsMode && includeShipping ? 1 : 0);

  // Auto-open the accordion when the sheet opens if any advanced filter is set.
  useEffect(() => {
    if (open && advancedFilterCount > 0) {
      setMoreOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeFilterCount =
    (selectedGame !== 'all' ? 1 : 0) +
    (category.length > 0 ? 1 : 0) +
    (priceMin !== 0 || priceMax < 1000 ? 1 : 0) +
    (isMyItemsMode && visibility ? visibility.length : 0) +
    advancedFilterCount;

  const triggerEl =
    trigger ?? (
      <Button
        variant="outline"
        className={cn(
          'h-10 px-3 transition-all focus-visible:ring-0 focus-visible:ring-offset-0',
          toolbarControl,
          '!bg-[hsl(var(--toolbar-control-bg,var(--background)))] hover:!bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
        )}
      >
        <Settings2 className="w-5 h-5" />
      </Button>
    );

  // Reusable "+ More" trailing chip that matches FilterChipRow's idle chip style.
  const MoreChip = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-sm border border-border/60 bg-white/[0.04] text-muted-foreground hover:text-foreground hover:bg-white/[0.07] transition-colors"
    >
      <Plus className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <MobileFilterSheetShell
      open={open}
      onOpenChange={setOpen}
      trigger={triggerEl}
      activeFilterCount={activeFilterCount}
      onClearFilters={onClearFilters}
    >
      {/* 1. Game — top TCGs + "More games" expander */}
      {showGameSection && (
        <FilterSection title="Game">
          <FilterChipRow options={gameOptions} value={selectedGame} onChange={setSelectedGame} />
          {!gamesExpanded && hasMoreGames && (
            <div className="mt-2">
              <MoreChip label="More games" onClick={() => setShowAllGames(true)} />
            </div>
          )}
        </FilterSection>
      )}

      {/* 2. Status (my-items only) */}
      {isMyItemsMode && setVisibility && (
        <FilterSection title="Status">
          <FilterChipMulti
            options={statusOptions}
            value={visibility || []}
            onChange={setVisibility}
          />
        </FilterSection>
      )}

      {/* 3. Category */}
      {showCategorySection && (
        <FilterSection title="Category">
          <FilterChipMulti options={categoryOptions.filter(o => o.value !== 'all')} value={category} onChange={setCategory} />
        </FilterSection>
      )}

      {/* 4. Language — top + "More languages" expander */}
      {showLanguageSection && (
        <FilterSection title="Language">
          <FilterChipMulti
            options={languageOptions}
            value={selectedLanguages}
            onChange={setSelectedLanguages}
          />
          {!languagesExpanded && hasMoreLanguages && (
            <div className="mt-2">
              <MoreChip
                label="More languages"
                onClick={() => setShowAllLanguages(true)}
              />
            </div>
          )}
        </FilterSection>
      )}

      {/* 5. Price */}
      <PriceRangeSection
        min={priceMin}
        max={priceMax}
        cap={1000}
        onChange={({ min, max }) => {
          setPriceMin(min);
          setPriceMax(max);
        }}
      />

      {/* 5. More filters — collapsible bucket for advanced/rarely-used controls */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="w-full flex items-center justify-between py-2.5 text-left"
          aria-expanded={moreOpen}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            More filters
            {advancedFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-active-gold/15 text-active-gold text-[10px] font-semibold normal-case tracking-normal">
                {advancedFilterCount}
              </span>
            )}
          </span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform',
              moreOpen && 'rotate-180',
            )}
          />
        </button>

        {moreOpen && (
          <div className="flex flex-col gap-5 pt-1">
            {/* Product type — only under Sealed */}
            {catSel === 'sealed' && showProductTypeRow && (
              <FilterSection title="Product type">
                <FilterChipRow
                  options={productTypeOptions}
                  value={productType}
                  onChange={setProductType}
                />
              </FilterSection>
            )}

            {/* Condition — hidden for slabs */}
            {showConditionSection && (
              <FilterSection title="Condition">
                <FilterChipRow options={conditionOptions} value={condition} onChange={setCondition} />
              </FilterSection>
            )}

            {/* Grading company + grade range — only when the seller has slabs */}
            {showGradingSections && (
              <>
                <FilterSection title="Grading company">
                  <GradingCompanyCombobox
                    value={gradingCompany === 'all' ? '' : gradingCompany}
                    onChange={(value) => setGradingCompany(value || 'all')}
                    placeholder="All companies"
                    allowedCompanies={availableGradingCompanies}
                    variant="toolbar"
                  />
                </FilterSection>

                <FilterSection title="Grade">
                  <div className="px-1">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[11px] text-muted-foreground">Range</span>
                      <span className="text-xs text-foreground/80">
                        {gradeMin === gradeMax ? gradeMin : `${gradeMin} – ${gradeMax}`}
                      </span>
                    </div>
                    <Slider
                      min={1}
                      max={10}
                      step={0.5}
                      value={[gradeMin, gradeMax]}
                      onValueChange={(values) => {
                        setGradeMin(values[0]);
                        setGradeMax(values[1]);
                      }}
                    />
                  </div>
                </FilterSection>
              </>
            )}

            {/* Location (browse mode only) */}
            {!isMyItemsMode && (
              <FilterSection title="Location">
                <LocationAutocomplete
                  value={location}
                  onChange={setLocation}
                  placeholder="Enter your city"
                />

                {location && (
                  <>
                    <div className="mt-3 px-1" style={{ touchAction: 'none' }}>
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[11px] text-muted-foreground">Within</span>
                        <span className="text-xs text-foreground/80">{currentDistanceLabel}</span>
                      </div>
                      <Slider
                        min={0}
                        max={DISTANCE_OPTIONS.length - 1}
                        step={1}
                        value={[currentDistanceIndex]}
                        onValueChange={(values) =>
                          setDistanceRadius(DISTANCE_OPTIONS[values[0]].value)
                        }
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setLocation(null)}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" /> Clear location
                    </button>
                  </>
                )}

                <div className="mt-3">
                  <FilterChipMulti
                    options={[{ value: 'ships', label: 'Include sellers who ship' }]}
                    value={includeShipping ? ['ships'] : []}
                    onChange={(v) => setIncludeShipping(v.includes('ships'))}
                  />
                </div>
              </FilterSection>
            )}

          </div>
        )}
      </div>
    </MobileFilterSheetShell>
  );
}
